import { DOCUMENT } from "@angular/common";
import {
  ApplicationRef,
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from "@angular/core";
import {
  SwUpdate,
  UnrecoverableStateEvent,
  VersionEvent,
} from "@angular/service-worker";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { filter, fromEvent, interval, merge, of, switchMap, take } from "rxjs";

import { LoggerService } from "./logger.service";

export const APP_UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;

export type AppUpdateStatus =
  | "idle"
  | "ready"
  | "installation-failed"
  | "unrecoverable";

/**
 * Coordinates Angular service-worker releases without reloading an operator's
 * active screen unexpectedly. A ready or broken cached version remains visible
 * until the operator explicitly chooses a full-page reload.
 */
@Injectable({ providedIn: "root" })
export class AppUpdateService {
  private readonly applicationRef = inject(ApplicationRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly swUpdate = inject(SwUpdate);
  private readonly logger = inject(LoggerService).withContext({
    service: "AppUpdateService",
  });

  private readonly _status = signal<AppUpdateStatus>("idle");
  private readonly _reloading = signal(false);
  private checkInFlight = false;

  readonly status = this._status.asReadonly();
  readonly reloading = this._reloading.asReadonly();
  readonly visible = computed(() => this._status() !== "idle");

  constructor() {
    if (!this.swUpdate.isEnabled) {
      this.logger.debug(
        "Service-worker updates are unavailable in this environment",
      );
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (event) => this.handleVersionEvent(event),
        error: (error: unknown) => {
          this.logger.error("Service-worker version event stream failed", {
            error: error instanceof Error ? error.message : String(error),
          });
        },
      });

    this.swUpdate.unrecoverable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.handleUnrecoverableState(event));

    this.startUpdateChecksWhenStable();
  }

  reloadForUpdate(): void {
    if (!this.visible() || this._reloading()) {
      return;
    }

    const window = this.document.defaultView;
    if (!window) {
      this.logger.error(
        "Application update reload failed: browser window unavailable",
      );
      return;
    }

    this._reloading.set(true);
    window.location.reload();
  }

  private startUpdateChecksWhenStable(): void {
    this.applicationRef.isStable
      .pipe(
        filter((isStable): isStable is true => isStable),
        take(1),
        switchMap(() =>
          merge(
            of(undefined),
            interval(APP_UPDATE_CHECK_INTERVAL_MS),
            fromEvent(this.document, "visibilitychange").pipe(
              filter(() => this.document.visibilityState === "visible"),
            ),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        void this.checkForUpdate();
      });
  }

  private async checkForUpdate(): Promise<void> {
    if (this.checkInFlight) {
      return;
    }

    this.checkInFlight = true;
    try {
      const updateFound = await this.swUpdate.checkForUpdate();
      if (updateFound && this._status() !== "unrecoverable") {
        this._status.set("ready");
      }
    } catch (error: unknown) {
      this.logger.warn("Service-worker update check failed", {
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      this.checkInFlight = false;
    }
  }

  private handleVersionEvent(event: VersionEvent): void {
    switch (event.type) {
      case "VERSION_DETECTED":
        this.logger.info("Downloading a new application version", {
          versionHash: event.version.hash,
        });
        break;
      case "VERSION_READY":
        if (this._status() !== "unrecoverable") {
          this._status.set("ready");
        }
        this.logger.info("Application update ready", {
          currentVersionHash: event.currentVersion.hash,
          latestVersionHash: event.latestVersion.hash,
        });
        break;
      case "VERSION_INSTALLATION_FAILED":
        if (this._status() === "idle") {
          this._status.set("installation-failed");
        }
        this.logger.error("Application update installation failed", {
          versionHash: event.version.hash,
          error: event.error,
        });
        break;
      case "NO_NEW_VERSION_DETECTED":
        break;
    }
  }

  private handleUnrecoverableState(event: UnrecoverableStateEvent): void {
    this._status.set("unrecoverable");
    this.logger.error("Cached application version is unrecoverable", {
      reason: event.reason,
    });
  }
}
